// Alert — inline banner per COMPONENTS §Catalog + §Per-primitive WCAG.
//
// 4 variants: success / warning / danger / info. role="alert" for
// danger+warning (interrupt politely); role="status" for success+info.
// Optional dismiss button — uses IconButton.

import type { ReactNode } from 'react';
import { icons } from './icons.js';
import { IconButton } from './IconButton.js';
import { Button } from './Button.js';
import { Spinner } from './Spinner.js';
import { useTranslation } from 'react-i18next';

export type AlertVariant = 'success' | 'warning' | 'danger' | 'info';

/** One act beside the words — "Load the latest" on a stale save, "Close" on a row that is gone (as Toast's). */
export interface AlertAction {
  label: string;
  onClick: () => void;
  /** The act is running (a fresh read on its way): the button spins, a second press waits. */
  busy?: boolean;
}

export interface AlertProps {
  variant: AlertVariant;
  children: ReactNode;
  /** Render an X close button. Caller controls visibility via parent state. */
  onDismiss?: () => void;
  action?: AlertAction;
  className?: string;
}

const variantStyle: Record<AlertVariant, { container: string; icon: ReactNode; role: 'alert' | 'status' }> = {
  success: {
    container: 'bg-[var(--color-success)]/10 border-[var(--color-success)]/40 text-[var(--color-text-primary)]',
    icon: <icons.statusSuccess aria-hidden weight="fill" className="text-[var(--color-success)] shrink-0" size={20} />,
    role: 'status',
  },
  warning: {
    container: 'bg-[var(--color-warning)]/10 border-[var(--color-warning)]/40 text-[var(--color-text-primary)]',
    icon: <icons.statusWarning aria-hidden weight="fill" className="text-[var(--color-warning)] shrink-0" size={20} />,
    role: 'alert',
  },
  danger: {
    container: 'bg-[var(--color-danger)]/10 border-[var(--color-danger)]/40 text-[var(--color-text-primary)]',
    icon: <icons.statusError aria-hidden weight="fill" className="text-[var(--color-danger)] shrink-0" size={20} />,
    role: 'alert',
  },
  info: {
    container: 'bg-[var(--color-info)]/10 border-[var(--color-info)]/40 text-[var(--color-text-primary)]',
    icon: <icons.statusInfo aria-hidden weight="fill" className="text-[var(--color-info)] shrink-0" size={20} />,
    role: 'status',
  },
};

export function Alert({ variant, children, onDismiss, action, className = '' }: AlertProps) {
  const { t } = useTranslation('common');
  const v = variantStyle[variant];
  return (
    <div
      role={v.role}
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 p-3 border rounded-md ${v.container} ${className}`}
    >
      {v.icon}
      {/* The words wrap and the act drops under them when the two cannot share the line (a phone, a long French verb). */}
      <div className="min-w-0 flex-1 wrap-anywhere text-sm">{children}</div>
      {action && (
        // The one act, as Toast draws its own: a ghost button, the padding overlapping so a one-line box stays one line.
        // Busy, it stays FOCUSABLE and announced inactive (aria-disabled, never the native attribute): the keyboard's
        // place must survive the press, so that when the act vanishes the line can take the focus it held.
        <Button
          variant="ghost"
          className="-my-2 ms-auto shrink-0"
          aria-disabled={action.busy || undefined}
          aria-busy={action.busy || undefined}
          onClick={action.busy ? undefined : action.onClick}
        >
          {action.busy && <Spinner size={16} />}
          {action.label}
        </Button>
      )}
      {onDismiss && (
        // -my-2 lets the 40px min-height tap target overlap the padding so it does
        // not inflate a single-line banner; items-center keeps everything centered.
        <IconButton aria-label={t('actions.dismiss')} variant="ghost" onClick={onDismiss} className="-my-2 -me-1">
          <icons.close aria-hidden size={16} />
        </IconButton>
      )}
    </div>
  );
}
